import React from 'react';
import PropTypes from 'prop-types';
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon';
import BullhornOutlineIcon from 'mdi-react/BullhornOutlineIcon';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import DatabaseImportIcon from 'mdi-react/DatabaseImportIcon';
import { partial } from '../../../util/translation/translation';
import FeatureShowcase from '../../../shared/components/custom/FeatureShowcase';
import PreviewMessages from '../../messages/components/PreviewsMessage';
import SpotlightPhonePreview from '../../Spotlight/components/SpotlightPhonePreview';

// A tile meant for one audience (any audience shows the "exclusive" star)
export const AUDIENCE_ACCESS = {
  group: [{ value: 'audience' }],
  level: null,
  tier: null,
};

// An example tile's image: a blue picture with the headline, made here so
// no file is needed (escaped, as it goes into SVG markup)
export const exampleImage = (headline) => {
  const text = `${headline}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0469DC"/><stop offset="1" stop-color="#0A3F86"/></linearGradient></defs><rect width="640" height="360" fill="url(#g)"/><circle cx="560" cy="60" r="140" fill="#ffffff" opacity="0.08"/><text x="40" y="300" font-family="Helvetica, Arial, sans-serif" font-size="40" font-weight="700" fill="#ffffff">${text}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

/** What segments are for: a push to one segment, and its own spotlight. */
const SegmentsShowcase = ({ collapsible, action }) => {
  const s = partial('shared');
  const onlyFor = s('segmentsShowcaseOnlyFor', {
    segment: s('segmentsShowcaseSegment'),
  });
  return (
    <FeatureShowcase
      storageKey="segmentsShowcaseHidden"
      icon={AccountMultipleIcon}
      title={s('segmentsInfoTitle')}
      subtitle={s('segmentsShowcaseSubtitle')}
      showLabel={s('segmentsShowcaseShow')}
      uses={[
        {
          key: 'push',
          icon: BellRingOutlineIcon,
          label: s('segmentsInfoPushLabel'),
          text: s('segmentsInfoPush'),
          // The Messages page's phone, with the club's logo
          preview: (
            <PreviewMessages
              formValues={{
                media: 'push',
                heading: s('segmentsShowcasePushTitle'),
                message: s('segmentsShowcasePushBody'),
              }}
            />
          ),
          chip: onlyFor,
        },
        {
          key: 'spotlight',
          icon: BullhornOutlineIcon,
          label: s('segmentsInfoSpotlightLabel'),
          text: s('segmentsInfoSpotlight'),
          // The Spotlight page's phone, with a tile for this segment
          preview: (
            <SpotlightPhonePreview
              group={{
                format: 'LANDSCAPE',
                size: 'MEDIUM',
                title: s('segmentsShowcaseBadge'),
                titleVisible: true,
                descriptionAvailable: true,
              }}
              tiles={[
                {
                  key: 'segment-example',
                  tile: {
                    title: s('segmentsShowcaseBannerTitle'),
                    description: s('segmentsShowcaseBannerText'),
                    headingImage: {
                      preview: exampleImage(s('segmentsShowcaseBannerTitle')),
                    },
                  },
                  access: AUDIENCE_ACCESS,
                },
              ]}
              ghost
            />
          ),
          chip: onlyFor,
        },
      ]}
      sourcesIcon={DatabaseImportIcon}
      sources={s('segmentsInfoSources')}
      collapsible={collapsible}
      action={action}
    />
  );
};

SegmentsShowcase.propTypes = {
  collapsible: PropTypes.bool,
  action: PropTypes.node,
};

SegmentsShowcase.defaultProps = {
  collapsible: true,
  action: null,
};

export default SegmentsShowcase;
